import React, { useEffect, useState } from 'react';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { Move } from 'lucide-react';

interface RectangleProps {
  shapeProps: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  onChange: (newAttrs: { x: number; y: number; width: number; height: number }) => void;
  parentBounds: { width: number; height: number };
}

const Rectangle: React.FC<RectangleProps> = ({ shapeProps, onChange, parentBounds }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [ghostPosition, setGhostPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        const newX = Math.max(0, Math.min(shapeProps.x + dx, parentBounds.width - shapeProps.width));
        const newY = Math.max(0, Math.min(shapeProps.y + dy, parentBounds.height - shapeProps.height));
        setGhostPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        onChange({ ...shapeProps, x: ghostPosition.x, y: ghostPosition.y });
      }
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, shapeProps, onChange, parentBounds, dragStart, ghostPosition]);

  const handleDragStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setGhostPosition({ x: shapeProps.x, y: shapeProps.y });
  };

  const handleResize = (_e: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => {
    onChange({
      ...shapeProps,
      width: size.width,
      height: size.height,
    });
  };

  return (
    <>
      <ResizableBox
        width={shapeProps.width}
        height={shapeProps.height}
        onResize={handleResize}
        minConstraints={[20, 20]}
        maxConstraints={[parentBounds.width - shapeProps.x, parentBounds.height - shapeProps.y]}
        style={{
          position: 'absolute',
          left: `${shapeProps.x}px`,
          top: `${shapeProps.y}px`,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            border: '1px solid #f30',
            backgroundColor: 'transparent',
            cursor: 'default',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-10px',
              left: '-10px',
              width: '20px',
              height: '20px',
              backgroundColor: 'white',
              borderRadius: '50%',
              border: '1px solid #f30',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'move',
            }}
            onMouseDown={handleDragStart}
          >
            <Move size={14} color="#f30" />
          </div>
        </div>
      </ResizableBox>

      {isDragging && (
        <div
          style={{
            position: 'absolute',
            left: `${ghostPosition.x}px`,
            top: `${ghostPosition.y}px`,
            width: `${shapeProps.width}px`,
            height: `${shapeProps.height}px`,
            border: '2px dashed #00ff00',
            backgroundColor: 'rgba(0, 255, 0, 0.1)',
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
};

export default Rectangle;
